import { useState } from 'react';
import { getStatusStyle } from '@/lib/capability-colors';
import { Clock, GitCommit } from 'lucide-react';
import type { CapabilityRevision } from '@/types';
import {
    MethodSchemaDrawer,
    type MethodSchemaDrawerPayload,
} from '@/components/capability-detail/MethodSchemaDrawer';

const GRID_STYLE = {
    gridTemplateColumns: 'repeat(auto-fill, minmax(265px, 322px))',
    gap: 'clamp(10px, 2vw, 20px)',
} as const;

const CARD_BASE =
    'relative z-0 flex min-h-[140px] w-full flex-col rounded-lg border border-slate-200 border-l-4 bg-white p-3 shadow-sm transition-shadow duration-150 hover:border-slate-300 hover:bg-slate-50';

function revisionHasTypeConfig(typeConfig: Record<string, unknown>) {
    return Object.keys(typeConfig).length > 0;
}

export function buildWorkingCopyManifest(params: {
    entityData: WorkingCopyEntityData;
    typeConfig: Record<string, unknown>;
}): Record<string, unknown> {
    const { entityData, typeConfig } = params;
    const manifest: Record<string, unknown> = {};

    manifest.name = entityData.fqcn || `${entityData.app_key}/${entityData.name}`;
    if (entityData.display_name) manifest.display_name = entityData.display_name;
    manifest.type = entityData.type;
    if (entityData.description) manifest.description = entityData.description;
    if (entityData.tags && entityData.tags.length > 0) manifest.tags = entityData.tags;
    if (entityData.visibility) manifest.visibility = entityData.visibility;
    if (entityData.aliases && entityData.aliases.length > 0) manifest.aliases = entityData.aliases;

    for (const [key, value] of Object.entries(typeConfig)) {
        if (!(key in manifest)) {
            manifest[key] = value;
        }
    }

    return manifest;
}

export interface WorkingCopyEntityData {
    name: string;
    app_key: string;
    fqcn?: string;
    display_name?: string | null;
    type: string;
    description?: string | null;
    tags?: string[];
    visibility?: string;
    aliases?: string[];
}

interface RevisionsTabProps {
    workingRevision?: CapabilityRevision | null;
    immutableRevisions?: CapabilityRevision[];
    entityData?: WorkingCopyEntityData;
}

export function RevisionsTab({ workingRevision, immutableRevisions, entityData }: RevisionsTabProps) {
    const [manifestDrawer, setManifestDrawer] = useState<MethodSchemaDrawerPayload | null>(null);

    return (
        <>
            <div className="space-y-6">
            {workingRevision && (
                    <div>
                        <h3 className="mb-3 text-sm font-semibold text-slate-900">Working Revision</h3>
                        <div className="grid justify-start" style={GRID_STYLE}>
                            <div className={`${CARD_BASE} border-l-amber-500`}>
                                <div className="flex min-h-0 w-full flex-1 flex-col">
                                    <div className="flex min-h-0 flex-1 flex-col">
                                        <div className="flex flex-row items-start gap-2">
                                            <div className="flex shrink-0 items-center justify-center rounded-md bg-amber-100 p-1.5 text-amber-600">
                                                <GitCommit size={20} />
                                            </div>
                                            <div className="flex min-w-0 flex-1 flex-col">
                                                <div className="flex flex-wrap items-center gap-1.5">
                                                    <span className="text-left font-mono text-[15.4px] font-bold leading-tight text-slate-800">
                                                        v{workingRevision.version_string}
                                                    </span>
                                                    <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[9.9px] font-medium uppercase text-amber-700">
                                                        working
                                                    </span>
                                                </div>
                                                {workingRevision.description && (
                                                    <p className="mt-2 line-clamp-2 text-left text-[11px] leading-tight text-slate-600">
                                                        {workingRevision.description}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    {(entityData || revisionHasTypeConfig(workingRevision.type_config)) && (
                                        <div className="border-t border-slate-100 pt-1.5">
                                            <details>
                                                <summary
                                                    className="cursor-pointer text-[11px] text-slate-400 transition-colors hover:text-slate-600"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        const schema = entityData
                                                            ? buildWorkingCopyManifest({
                                                                entityData,
                                                                typeConfig: workingRevision.type_config,
                                                            })
                                                            : workingRevision.type_config;
                                                        setManifestDrawer({
                                                            methodName: `v${workingRevision.version_string}`,
                                                            schema,
                                                            kind: 'type_config',
                                                        });
                                                    }}
                                                >
                                                    Manifest
                                                </summary>
                                            </details>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {immutableRevisions && immutableRevisions.length > 0 && (
                    <div>
                        <h3 className="mb-3 text-sm font-semibold text-slate-900">Published Versions</h3>
                        <div className="grid justify-start" style={GRID_STYLE}>
                        {immutableRevisions.map((rev) => {
                            const statusStyle = getStatusStyle(rev.status);
                            return (
                                <div
                                    key={rev.id}
                                    className={`${CARD_BASE} border-l-indigo-500`}
                                >
                                    <div className="flex min-h-0 w-full flex-1 flex-col">
                                        <div className="flex min-h-0 flex-1 flex-col">
                                            <div className="flex flex-row items-start gap-2">
                                                <div className="flex shrink-0 items-center justify-center rounded-md bg-indigo-100 p-1.5 text-indigo-600">
                                                    <GitCommit size={20} />
                                                </div>
                                                <div className="flex min-w-0 flex-1 flex-col">
                                                    <div className="flex flex-wrap items-center gap-1.5">
                                                        <span className="text-left font-mono text-[15.4px] font-bold leading-tight text-slate-800">
                                                            v{rev.version_string}
                                                        </span>
                                                        <span
                                                            className={`rounded-full px-1.5 py-0.5 text-[9.9px] font-medium uppercase ${statusStyle}`}
                                                        >
                                                            {rev.status}
                                                        </span>
                                                    </div>
                                                    {rev.deprecation_reason && (
                                                        <p className="mt-2 line-clamp-2 text-left text-[11px] italic leading-tight text-slate-500">
                                                            {rev.deprecation_reason}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-1 border-t border-slate-100 pt-1.5 text-[11px] text-slate-500">
                                            <span className="ml-auto flex items-center gap-1 text-slate-400">
                                                <Clock size={11} />
                                                {new Date(rev.created).toLocaleDateString()}
                                            </span>
                                        </div>
                                        {revisionHasTypeConfig(rev.type_config) && (
                                            <div className="border-t border-slate-100 pt-1.5">
                                                <details>
                                                    <summary
                                                        className="cursor-pointer text-[11px] text-slate-400 transition-colors hover:text-slate-600"
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            setManifestDrawer({
                                                                methodName: `v${rev.version_string}`,
                                                                schema: rev.type_config,
                                                                kind: 'type_config',
                                                            });
                                                        }}
                                                    >
                                                        Manifest
                                                    </summary>
                                                </details>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {!workingRevision &&
                (!immutableRevisions || immutableRevisions.length === 0) && (
                    <div className="flex h-64 items-center justify-center rounded-lg border border-dashed border-slate-300 bg-slate-50">
                        <div className="flex flex-col items-center gap-3">
                            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-slate-100">
                                <GitCommit size={24} className="text-slate-400" />
                            </div>
                            <p className="text-[15.4px] text-slate-400">No revisions found</p>
                        </div>
                    </div>
                )}
        </div>
        <MethodSchemaDrawer
            open={manifestDrawer !== null}
            onOpenChange={(open) => {
                if (!open) setManifestDrawer(null);
            }}
            payload={manifestDrawer}
        />
        </>
    );
}
